import moment from 'moment'
import React, { useCallback, createContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'

import { customerWebConfigurationUrl } from '../../constants/inputLocalization'
import { api } from '../../modules/api'
import { fetchingLanguageStart, fetchingLanguageEnd } from '../../modules/Common/actions'
import { clearAllNotificationFilters } from '../../modules/Filters/actions'
import { clearManageUpdatesFilter, clearProductFilters } from '../../modules/Products/actions'
import { clearPromotionFilters } from '../../modules/Promotions/actions'
import { clearNotificationsFilter } from '../../modules/PushNotifications/actions'
import { clearStoreFilters } from '../../modules/stores/actions'

import {
    formatString,
    formatStringGroupAsArray,
    language,
    languages,
    loadLanguage,
    rtl,
    setAvailableLanguages,
    strings,
} from './strings'

export const LanguageContext = createContext()

const LanguageContextProvider = ({ children }) => {
    // Constants

    const dispatch = useDispatch()

    // States

    const [loading, setLoading] = useState(false)
    const [firstLoad, setFirstLoad] = useState(true)

    const [lastUpdate, setLastUpdate] = useState(new Date().toISOString())

    const languageState = useSelector(({ common }) => common.adp_configurations).language
    const idToken = useSelector(({ auth }) => auth.idToken)

    const [customerWebLanguages, setCustomerWebLanguages] = useState([])

    const location = useLocation()

    // Helpers

    const updateLanguage = useCallback(
        async (langs, options = {}) => {
            const { persist = true } = options
            setLoading(true)

            dispatch(fetchingLanguageStart())
            let error
            try {
                const lang = await loadLanguage(langs)
                if (persist) {
                    localStorage.setItem('language', lang)
                }
                setLastUpdate(new Date().toISOString())

                // Set moment global language
                moment.locale(lang)
            } catch (err) {
                error = err
            }

            // CLEAR REDUX LANGUAGE STATES
            // Promotions Filters
            dispatch(clearPromotionFilters())
            // Push Notification Filters
            dispatch(clearAllNotificationFilters())
            dispatch(clearNotificationsFilter())
            // Ops Tools Store Filters
            dispatch(clearStoreFilters())
            // Products Filters
            dispatch(clearProductFilters())
            // Assortment Manage Updates Filters
            dispatch(clearManageUpdatesFilter())

            dispatch(fetchingLanguageEnd())

            setLoading(false)

            if (error) throw error
        },
        [dispatch]
    )

    // Hooks

    useEffect(() => {
        ;(async () => {
            // TODO: Temporal Fix as configuration languages might no be retrieved
            let tmpLanguageState = [
                {
                    data: {
                        default: 'en',
                        languages: [{ code: 'en' }],
                    },
                },
            ]
            if (languageState) tmpLanguageState = languageState

            setLoading(true)

            const oldLanguages = languages.toString()
            const languageData = tmpLanguageState[0]?.data
            if (oldLanguages !== languageData.languages.toString() || firstLoad) {
                setFirstLoad(false)
                await setAvailableLanguages(languageData)

                const search = location.search
                const query = new URLSearchParams(search)
                const queryLanguage = query.get('lang')
                const localStorageLanguage = localStorage.getItem('language')
                // TODO: MISSING USER PROFILE LANGUAGE
                try {
                    const usingFallback = !languageState
                    await updateLanguage(
                        { queryLanguage, localStorageLanguage },
                        { persist: !usingFallback }
                    )
                } catch (err) {
                    console.error(err)
                }
            }

            setLoading(false)
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [languageState, location.search, updateLanguage])

    useEffect(() => {
        if (!idToken.length) return
        else
            (async () => {
                setLoading(true)

                try {
                    const { data } = await api(customerWebConfigurationUrl, 'POST', {
                        app: '7NOW',
                        type: 'global',
                        version: '1.0',
                        country: 'US',
                        last_modified_date: '',
                    })

                    const feature = data.configurations.find(
                        o => o.properties[0]?.id === 'language'
                    )
                    const { default: resDefaultLanguage, languages: resLanguages } =
                        feature.properties[0].default_value
                    const result = resLanguages.flatMap(l =>
                        l.code !== resDefaultLanguage ? [l.code] : []
                    )

                    setCustomerWebLanguages({ languages: result, default: resDefaultLanguage })
                } catch (error) {
                    console.error(error)
                }

                setLoading(false)
            })()
    }, [idToken])

    // Render

    const contextValues = {
        customerWebLanguages,
        formatString,
        formatStringGroupAsArray,
        language,
        languages,
        lastUpdate,
        loading,
        strings,
        updateLanguage,
        rtl,
    }

    return <LanguageContext.Provider value={contextValues}>{children}</LanguageContext.Provider>
}

export default LanguageContextProvider
